import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useThemeLoader } from './hooks/useThemeLoader';
import { RootState } from './store';
import LoadingSpinner from './components/ui/LoadingSpinner';

// Lazy-loaded routes
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const DashboardLayout = lazy(() => import('./dashboard/DashboardLayout'));
const AdminLayout = lazy(() => import('./dashboard/AdminLayout'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const GoogleSuccessPage = lazy(() => import('./pages/auth/GoogleSuccessPage'));
const LandingPage = lazy(() => import('./pages/LandingPage'));

// Legal
const TermsPage = lazy(() => import('./pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage'));

// Dashboard pages (viewer)
const DashboardHome = lazy(() => import('./dashboard/pages/DashboardHome'));
const DashboardHero = lazy(() => import('./dashboard/pages/DashboardHero'));
const DashboardSpecialty = lazy(() => import('./dashboard/pages/DashboardSpecialty'));
const DashboardEducation = lazy(() => import('./dashboard/pages/DashboardEducation'));
const DashboardCertificates = lazy(() => import('./dashboard/pages/DashboardCertificates'));
const DashboardExperience = lazy(() => import('./dashboard/pages/DashboardExperience'));
const DashboardSkills = lazy(() => import('./dashboard/pages/DashboardSkills'));
const DashboardPortfolio = lazy(() => import('./dashboard/pages/DashboardPortfolio'));
const DashboardFooter = lazy(() => import('./dashboard/pages/DashboardFooter'));
const DashboardTheme = lazy(() => import('./dashboard/pages/DashboardTheme'));
const DashboardSiteConfig = lazy(() => import('./dashboard/pages/DashboardSiteConfig'));
const DashboardProfile = lazy(() => import('./dashboard/pages/DashboardProfile'));
const DashboardSectionVisibility = lazy(() => import('./dashboard/pages/DashboardSectionVisibility'));

// Admin
const AdminDashboard = lazy(() => import('./dashboard/pages/AdminDashboard'));

// ── Route guards ──────────────────────────────────────────────────────────────

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useSelector((s: RootState) => s.auth);
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  return <>{children}</>;
}

function ViewerRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useSelector((s: RootState) => s.auth);
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  if ((user as any)?.role === 'admin') return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useSelector((s: RootState) => s.auth);
  if (!isAuthenticated) return <Navigate to="/auth/login" replace />;
  if ((user as any)?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  useThemeLoader();

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />

        {/* Public portfolio */}
        <Route path="/portfolio/:username" element={<PortfolioPage />} />

        {/* Auth */}
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        {/* Google OAuth callback — must be publicly accessible, no auth guard */}
        <Route path="/auth/google/success" element={<GoogleSuccessPage />} />

        {/* Legal */}
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminDashboard />} />
        </Route>

        {/* Viewer dashboard */}
        <Route path="/dashboard" element={<ViewerRoute><DashboardLayout /></ViewerRoute>}>
          <Route index element={<DashboardHome />} />
          <Route path="profile" element={<DashboardProfile />} />
          <Route path="hero" element={<DashboardHero />} />
          <Route path="specialty" element={<DashboardSpecialty />} />
          <Route path="education" element={<DashboardEducation />} />
          <Route path="certificates" element={<DashboardCertificates />} />
          <Route path="experience" element={<DashboardExperience />} />
          <Route path="skills" element={<DashboardSkills />} />
          <Route path="portfolio" element={<DashboardPortfolio />} />
          <Route path="footer" element={<DashboardFooter />} />
          <Route path="theme" element={<DashboardTheme />} />
          <Route path="site-config" element={<DashboardSiteConfig />} />
          <Route path="section-visibility" element={<DashboardSectionVisibility />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}